import React, { useState, useMemo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { TabComponent, TabItemDirective, TabItemsDirective } from '@syncfusion/ej2-react-navigations';
import { DatePickerComponent, DateTimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject } from '@syncfusion/ej2-react-grids';
import { setCulture } from '@syncfusion/ej2-base';
import { useTranslation } from 'react-i18next';
import { useWizardContext } from '../../contexts/useWizardContext';
import type { Step1DemandCellClear, Step1FlushPendingEditsResult } from '../../contexts/ProductionScheduleWizardContext';
import { DemandView } from '../DemandView';
import { CsvImportDialog } from '../CsvImportDialog';
import { demandEntityFields } from '../../config/entityFields';
import { ProductionScheduleControllerService } from '../../api/services/ProductionScheduleControllerService';
import type { DemandDto } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useHasPermission } from '../../utils/permissions';
import { useErrorHandler } from '../../utils/errorHandler';
import { useCsvImportConfig } from '../../hooks/useCsvImportConfig';
import {
  parseDemandImportOptions,
  normalizeDemandQuantity,
  filterDemandsByAllowedMaterials,
  aggregateDemandRows,
  buildOrderDerivedForecasts
} from '../../utils/demandCsvImportPipeline';
import {
  filterReplanningProductMaterials,
  buildReplanningGridRows,
} from '../../utils/replanningGridUtils';
import { getEffectiveMaterialId } from '../../utils/aliasProductUtils';
import { parseDemandCellInput } from '../../utils/demandCellInput';
import './PlanningHorizon.css';

const PlanningHorizon: React.FC = () => {
  const { t, i18n } = useTranslation(['common']);
  const hasDemandCreatePermission = useHasPermission('demand:create');
  const {
    formData,
    planningSettings,
    isLoadingDemands,
    orders,
    forecasts,
    productionDisplayDemandsAggregated,
    productionEffectiveDemands,
    materials,
    updateFormData,
    convertDemandsForView,
    addOrUpdatePendingOrder,
    addOrUpdatePendingForecast,
    clearPendingDemandCell,
    addPendingOrdersFromImport,
    addPendingForecastsFromImport,
    pendingOrders,
    pendingForecasts,
    planningMode,
    sourceScheduleUuid,
    frozenZoneEnd,
    setFrozenZoneEnd,
    setStep1ReplanningHasNewSollBelowIst,
    setLockedReplanningMaterialIds,
    registerStep1FlushPendingEdits
  } = useWizardContext();

  const isReplanningView = planningMode === 'REPLANNING';
  const { showToast } = useToast();
  const { handleError } = useErrorHandler();
  const { config: demandsCsvConfig } = useCsvImportConfig('demands');
  const [importDialogType, setImportDialogType] = useState<'orders' | 'forecasts' | null>(null);
  const [replanningImportOpen, setReplanningImportOpen] = useState(false);
  const replanningGridRef = useRef<GridComponent>(null);
  const [producedQuantitiesRows, setProducedQuantitiesRows] = useState<Array<{ materialId: string; materialName: string; istQuantity: number; oldSollQuantity: number; isLocked?: boolean; campaignStatus?: 'OPEN' | 'ACTIVE' | 'COMPLETED'; startedDoughBatchCount?: number; totalDoughBatchCount?: number }>>([]);
  const [loadingProducedQuantities, setLoadingProducedQuantities] = useState(false);
  const [sourceScheduleWindow, setSourceScheduleWindow] = useState<{ min: Date; max: Date; pickerMin: Date } | null>(null);
  /** When min > max: replanning is no longer possible -> point at follow-on planning */
  const [frozenZoneUnavailableReason, setFrozenZoneUnavailableReason] = useState<'REPLAN_NOT_POSSIBLE' | null>(null);
  // Pending value for the frozen zone end; applied only on the refresh button click
  const [pendingFrozenZoneEnd, setPendingFrozenZoneEnd] = useState<string | null>(null);
  const frozenZonePickerRef = React.useRef<any>(null);

  // Tab refs (used only in non-REPLANNING mode, but must be declared unconditionally per Rules of Hooks)
  const tabRef = useRef<any>(null);
  const panel0Ref = useRef<HTMLDivElement>(null);
  const panel1Ref = useRef<HTMLDivElement>(null);
  const panel2Ref = useRef<HTMLDivElement>(null);
  const panel3Ref = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef(0);
  const ordersGridRef = useRef<any>(null);
  const forecastsGridRef = useRef<any>(null);
  const intermediatesGridRef = useRef<any>(null);
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(() => new Set([0]));

  useLayoutEffect(() => {
    if (planningMode !== 'REPLANNING') {
      if (panel1Ref.current) panel1Ref.current.style.display = 'none';
      if (panel2Ref.current) panel2Ref.current.style.display = 'none';
      if (panel3Ref.current) panel3Ref.current.style.display = 'none';
    }
  }, [planningMode]);

  const pendingOrdersRef = useRef(pendingOrders);
  pendingOrdersRef.current = pendingOrders;
  const pendingForecastsRef = useRef(pendingForecasts);
  pendingForecastsRef.current = pendingForecasts;
  /** quantity null = clear the cell (no record); 0 = an explicit zero quantity */
  type LocalEdit = {
    demandType: 'ORDER' | 'FORECAST';
    materialId: string;
    materialName: string;
    dateStr: string;
    quantity: number | null;
    existingUuid?: string;
  };
  const localEditsRef = useRef<Map<string, LocalEdit>>(new Map());

  const flushToContext = useCallback(() => {
    const edits = localEditsRef.current;
    if (edits.size === 0) return;
    edits.forEach(({ demandType, materialId, materialName, dateStr, quantity, existingUuid }) => {
      if (quantity === null) {
        clearPendingDemandCell({ demandType, materialId, dateStr, existingUuid });
      } else {
        const demand: DemandDto = {
          // The uuid of the edited demand makes the save an update (review round 5, finding 1.1)
          uuid: existingUuid,
          materialId,
          materialName,
          demandDate: `${dateStr}T00:00:00`,
          dueTime: undefined,
          quantity,
          demandType
        };
        if (demandType === 'ORDER') {
          addOrUpdatePendingOrder(demand, existingUuid);
        } else {
          addOrUpdatePendingForecast(demand, existingUuid);
        }
      }
    });
    edits.clear();
  }, [addOrUpdatePendingOrder, addOrUpdatePendingForecast, clearPendingDemandCell]);

  const flushLocalEdits = useCallback((): Step1FlushPendingEditsResult => {
    const edits = localEditsRef.current;
    if (edits.size === 0) return { saves: [], clears: [] };
    const saves: DemandDto[] = [];
    const clears: Step1DemandCellClear[] = [];
    edits.forEach(({ demandType, materialId, materialName, dateStr, quantity, existingUuid }) => {
      if (quantity === null) {
        clears.push({ demandType, materialId, dateStr, existingUuid });
      } else {
        saves.push({
          uuid: existingUuid,
          materialId,
          materialName,
          demandDate: `${dateStr}T00:00:00`,
          dueTime: undefined,
          quantity,
          demandType
        });
      }
    });
    edits.clear();

    // Project manually entered forecasts (demandType=FORECAST) N weeks into the future on the same
    // weekday - for products AND intermediate products alike. Orders (ORDER) are deliberately NOT
    // projected. The value comes from the planning settings (intermediateForecastWeeksAhead, a
    // shared horizon, default 4). Forecasts are deliberately persisted BEYOND plannedEnd as well -
    // the next plan then sees them pre-populated.
    const weeksAhead = planningSettings.intermediateForecastWeeksAhead ?? 0;
    if (weeksAhead > 0) {
      const forecastSeed = saves.filter((s) => s.demandType === 'FORECAST' && s.materialId);
      if (forecastSeed.length > 0) {
        const propagated = buildOrderDerivedForecasts(forecastSeed, weeksAhead);
        saves.push(...propagated);
      }
    }

    return { saves, clears };
  }, [planningSettings.intermediateForecastWeeksAhead]);

  useEffect(() => {
    registerStep1FlushPendingEdits(flushLocalEdits);
    return () => registerStep1FlushPendingEdits(null);
  }, [registerStep1FlushPendingEdits, flushLocalEdits]);

  // DateTimePicker (Frozen Zone) uses Syncfusion culture for month names; use "de" to match loaded CLDR (main/de/)
  useEffect(() => {
    if (planningMode === 'REPLANNING' && i18n.language.startsWith('de')) {
      setCulture('de');
    }
  }, [planningMode, i18n.language]);

  // Replanning: frozen zone window from the backend (min = NOW, max = the latest start of a first operation per batch; server zone)
  React.useEffect(() => {
    if (planningMode !== 'REPLANNING' || !sourceScheduleUuid) {
      setSourceScheduleWindow(null);
      setFrozenZoneUnavailableReason(null);
      return;
    }
    let cancelled = false;
    setFrozenZoneUnavailableReason(null);
    ProductionScheduleControllerService.getFrozenZoneWindow(sourceScheduleUuid)
      .then((window) => {
        if (cancelled || !window?.min || !window?.max) return;
        const minDate = new Date(window.min.replace('Z', '').trim());
        const maxDate = new Date(window.max.replace('Z', '').trim());
        if (isNaN(minDate.getTime()) || isNaN(maxDate.getTime())) return;
        if (minDate.getTime() >= maxDate.getTime()) {
          setFrozenZoneUnavailableReason('REPLAN_NOT_POSSIBLE');
          setSourceScheduleWindow(null);
          return;
        }
        setSourceScheduleWindow({ min: minDate, max: maxDate, pickerMin: minDate });
      })
      .catch(() => { if (!cancelled) { setSourceScheduleWindow(null); setFrozenZoneUnavailableReason(null); } });
    return () => { cancelled = true; };
  }, [planningMode, sourceScheduleUuid]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');
  const tomorrowString = `${year}-${month}-${day}`;
  const tomorrowDateTimeString = `${tomorrowString}T00:00:00`;
  const startMaxDaysAhead = planningSettings.startMaxDaysAhead ?? 28;
  const maxStartDate = new Date(today);
  maxStartDate.setDate(maxStartDate.getDate() + startMaxDaysAhead);
  maxStartDate.setHours(23, 59, 59, 999);
  const planningHorizonStart = formData.plannedStart ? formData.plannedStart.split('T')[0] : tomorrowString;
  const planningHorizonEnd = formData.plannedEnd ? formData.plannedEnd.split('T')[0] : tomorrowString;
  const isSingleDayHorizon = planningSettings.maxDays === 1;
  // Display week for the demands: 7 days from the chosen planning start
  const displayWeekStartStr = planningHorizonStart;
  const displayWeekEndDate = new Date(displayWeekStartStr + 'T12:00:00');
  displayWeekEndDate.setDate(displayWeekEndDate.getDate() + 6);
  const displayWeekEndStr = `${displayWeekEndDate.getFullYear()}-${String(displayWeekEndDate.getMonth() + 1).padStart(2, '0')}-${String(displayWeekEndDate.getDate()).padStart(2, '0')}`;

  // Replan: plannedStart is inherited from the source plan in Step 0 (alongside plannedEnd).
  // We deliberately do NOT override it with frozenZoneEnd here. Doing so would shift the
  // demand-window [plannedStart, plannedEnd] back to the FZ day, pulling in demands from
  // days that don't belong to the source plan (e.g. yesterday's orders when FZ is set today).

  // Initial default: when no start is set (and this is not a replan), start = tomorrow.
  React.useEffect(() => {
    if (planningMode === 'REPLANNING' && frozenZoneEnd) return;
    if (!formData.plannedStart) {
      updateFormData('plannedStart', tomorrowDateTimeString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formData.plannedStart intentionally excluded to avoid re-setting start on every change
  }, [tomorrowDateTimeString, planningMode, frozenZoneEnd, updateFormData]);

  // With maxDays === 1: always set the end to the same day as the start (23:59:59).
  React.useEffect(() => {
    // REPLANNING: plannedEnd = delivery day of the source plan (set in step 0), do not overwrite.
    if (!isSingleDayHorizon || !formData.plannedStart || planningMode === 'REPLANNING') return;
    const startDateStr = formData.plannedStart.split('T')[0];
    const endOfDay = `${startDateStr}T23:59:59`;
    if (formData.plannedEnd !== endOfDay) {
      updateFormData('plannedEnd', endOfDay);
    }
  }, [isSingleDayHorizon, planningMode, formData.plannedStart, formData.plannedEnd, updateFormData]);

  // Replanning: load the produced quantities until the end of the frozen zone
  React.useEffect(() => {
    if (planningMode !== 'REPLANNING' || !sourceScheduleUuid || !frozenZoneEnd) {
      setProducedQuantitiesRows([]);
      setLockedReplanningMaterialIds(new Set());
      return;
    }
    let cancelled = false;
    setLoadingProducedQuantities(true);
    const effectiveAt = frozenZoneEnd.replace('Z', '').trim();
    ProductionScheduleControllerService.getProducedQuantitiesUntil(sourceScheduleUuid, effectiveAt)
      .then((rows) => {
        if (cancelled) return;
        const normalized = (rows ?? []).map((r) => ({
          materialId: r.materialId ?? '',
          materialName: r.materialName ?? '',
          istQuantity: r.istQuantity ?? 0,
          oldSollQuantity: r.oldSollQuantity ?? 0,
          isLocked: r.isLocked,
          campaignStatus: r.campaignStatus,
          startedDoughBatchCount: r.startedDoughBatchCount,
          totalDoughBatchCount: r.totalDoughBatchCount
        }));
        setProducedQuantitiesRows(normalized);
        const lockedIds = new Set(normalized.filter(r => r.isLocked).map(r => r.materialId));
        setLockedReplanningMaterialIds(lockedIds);
      })
      .catch(() => {
        if (!cancelled) {
          setProducedQuantitiesRows([]);
          setLockedReplanningMaterialIds(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingProducedQuantities(false);
      });
    return () => { cancelled = true; };
  }, [planningMode, sourceScheduleUuid, frozenZoneEnd, setLockedReplanningMaterialIds]);


  // Import orders (ORDER) or forecasts (FORECAST) - like manual entry: only written to the database on "Next"
  const handleImportDemands = async (data: DemandDto[], demandType: 'ORDER' | 'FORECAST') => {
    try {
      const opts = parseDemandImportOptions(demandsCsvConfig);
      const cleaned = data.map((d: DemandDto) => {
        const { id: _id, uuid: _uuid, ...rest } = d as DemandDto & { id?: string };
        return { ...rest, uuid: undefined, demandType, quantity: normalizeDemandQuantity(rest.quantity) } as DemandDto;
      });
      const { rows: afterWhitelist, skipped: skippedWhitelist } = filterDemandsByAllowedMaterials(
        cleaned,
        opts.allowedMaterialUuids
      );
      const aggregated = aggregateDemandRows(afterWhitelist);

      if (demandType === 'ORDER') {
        // Orders are deliberately NOT projected.
        addPendingOrdersFromImport(aggregated);
      } else {
        addPendingForecastsFromImport(aggregated);
        // Project forecasts N weeks ahead on the same weekday (products and intermediate products).
        if (opts.orderForecastWeeksAhead > 0) {
          const derived = buildOrderDerivedForecasts(aggregated, opts.orderForecastWeeksAhead);
          addPendingForecastsFromImport(derived);
        }
      }
      const hints: string[] = [];
      if (skippedWhitelist > 0) {
        hints.push(
          t('csvImport.demandImportSkippedNotAllowed', '{{count}} Zeilen: Material nicht in der freigegebenen Liste.', {
            count: skippedWhitelist
          })
        );
      }
      if (demandType === 'FORECAST' && opts.orderForecastWeeksAhead > 0 && aggregated.length > 0) {
        hints.push(
          t('csvImport.demandImportDerivedForecastsHint', 'Prognosen für {{weeks}} Woche(n) ergänzt.', {
            weeks: opts.orderForecastWeeksAhead
          })
        );
      }
      showToast(
        'success',
        t('csvImport.import'),
        hints.length > 0 ? hints.join(' ') : t('csvImport.importSuccess')
      );
    } catch (error) {
      handleError(error, 'importing demands');
      throw error;
    }
  };

  // REPLANNING: the delivery day of a material = production day (plannedEnd of the source plan) + longTermOffsetDays.
  // longRunning and the offset live on the main product; demands may sit on the alias UUID. For products that are
  // not long-term managed the offset is 0 -> delivery day = plannedEnd. That way the production quantities
  // logic (productionEffectiveDemands) pulls the demand back to plannedEnd when reading.
  const deliveryDateStrForMaterial = useCallback((materialId: string | undefined): string => {
    const plannedEndStr = (formData.plannedEnd || frozenZoneEnd || '')?.split('T')[0] || '';
    if (!plannedEndStr || !materialId) return plannedEndStr;
    const mainId = getEffectiveMaterialId(materialId, materials) || materialId;
    const mainMat = materials.find((m: { uuid?: string }) => m.uuid === mainId) as
      | { longRunning?: boolean; longTermOffsetDays?: number }
      | undefined;
    const offset = mainMat?.longRunning === true ? Math.max(0, Number(mainMat?.longTermOffsetDays) || 0) : 0;
    if (offset === 0) return plannedEndStr;
    const [y, m, d] = plannedEndStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + offset);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  }, [formData.plannedEnd, frozenZoneEnd, materials]);

  // Replanning: CSV import for orders. Only the configured whitelist (permitted materials) filters
  // products - long-term managed ones are NO longer skipped wholesale, so that their offset is
  // planned in. Locked campaigns are validated only on "Next".
  const handleReplanningImport = useCallback(
    async (data: DemandDto[]) => {
      try {
        const opts = parseDemandImportOptions(demandsCsvConfig);
        // Every imported demand is booked onto its delivery day = production day (plannedEnd of the
        // source plan) + longTermOffsetDays. For products that are not long-term managed that is plannedEnd.
        // The user does not have to enter the date by hand in the run parameters.
        const cleaned = data.map((d: DemandDto) => {
          const { id: _id, uuid: _uuid, ...rest } = d as DemandDto & { id?: string };
          const deliveryDate = deliveryDateStrForMaterial(rest.materialId);
          return {
            ...rest,
            uuid: undefined,
            demandType: 'ORDER' as const,
            quantity: normalizeDemandQuantity(rest.quantity),
            ...(deliveryDate ? { demandDate: `${deliveryDate}T00:00:00` } : {}),
          } as DemandDto;
        });
        const { rows: afterWhitelist, skipped: skippedWhitelist } = filterDemandsByAllowedMaterials(
          cleaned,
          opts.allowedMaterialUuids
        );
        const aggregated = aggregateDemandRows(afterWhitelist);
        // Do not import locked products (whose first operation is already finished)
        const lockedMaterialIds = new Set(
          producedQuantitiesRows.filter(r => r.isLocked).map(r => r.materialId)
        );
        const withoutLocked = lockedMaterialIds.size > 0
          ? aggregated.filter(d => !lockedMaterialIds.has(d.materialId ?? ''))
          : aggregated;
        const skippedLocked = aggregated.length - withoutLocked.length;
        const lockedSkippedNames = skippedLocked > 0
          ? [...new Set(aggregated
              .filter(d => lockedMaterialIds.has(d.materialId ?? ''))
              .map(d => d.materialName || d.materialId || ''))]
          : [];
        addPendingOrdersFromImport(withoutLocked);
        // No derived forecasts on a replan - only ORDER quantities are set, for the delivery day.
        const hints: string[] = [];
        if (skippedWhitelist > 0) {
          hints.push(
            t('csvImport.demandImportSkippedNotAllowed', '{{count}} Zeilen: Material nicht in der freigegebenen Liste.', {
              count: skippedWhitelist
            })
          );
        }
        if (skippedLocked > 0) {
          hints.push(
            t(
              'productionSchedule.wizard.step1.replanning.importSkippedLocked',
              '{{count}} gesperrte Produkte übersprungen: {{names}}.',
              { count: skippedLocked, names: lockedSkippedNames.join(', ') }
            )
          );
        }
        showToast(
          'success',
          t('csvImport.import'),
          hints.length > 0 ? hints.join(' ') : t('csvImport.importSuccess')
        );
      } catch (error) {
        handleError(error, 'importing demands');
        throw error;
      }
    },
    [
      demandsCsvConfig,
      deliveryDateStrForMaterial,
      producedQuantitiesRows,
      addPendingOrdersFromImport,
      showToast,
      t,
      handleError
    ]
  );

  // Filter orders and forecasts for tab display: 7 days from the chosen planning start
  const ordersInWeek = orders.filter((d: DemandDto) => {
    const dDate = d.demandDate?.split('T')[0];
    return dDate && dDate >= displayWeekStartStr && dDate <= displayWeekEndStr;
  });
  const forecastsInWeek = forecasts.filter((d: DemandDto) => {
    const dDate = d.demandDate?.split('T')[0];
    return dDate && dDate >= displayWeekStartStr && dDate <= displayWeekEndStr;
  });

  const weekDateColumns = useMemo(() => {
    const cols: string[] = [];
    const base = new Date(displayWeekStartStr + 'T12:00:00');
    for (let i = 0; i < 7; i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      cols.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
    return cols;
  }, [displayWeekStartStr]);

  /** As in the production quantities tab: weekday plus date (e.g. "Freitag, 20.02.2026"). */
  const formatDateForGrid = (dateStr: string) => {
    if (!dateStr) return '';
    const d = dateStr.split('T')[0];
    return new Date(d + 'T12:00:00').toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Build matrix data for inline-editable grid: rows = materials, columns = dates
  // Includes committed pending data (context state) + uncommitted local edits (ref)
  // materialFilter: 'PRODUCT' for the orders and forecasts tabs, 'INTERMEDIATE_PRODUCT' for the intermediate products tab.
  const getGridDataForType = useCallback((
    demandType: 'ORDER' | 'FORECAST',
    materialFilter: 'PRODUCT' | 'INTERMEDIATE_PRODUCT' = 'PRODUCT',
  ) => {
    const demandsList = demandType === 'ORDER' ? ordersInWeek : forecastsInWeek;
    const pendingList = demandType === 'ORDER' ? pendingOrdersRef.current : pendingForecastsRef.current;
    const byKey = new Map<string, DemandDto>();
    const key = (m: string, d: string) => `${m}|${d}`;
    const allowedMaterialIds = new Set(
      materials
        .filter((m: any) => m.materialType === materialFilter)
        .map((m: any) => m.uuid as string)
    );
    demandsList.forEach((d: DemandDto) => {
      const dStr = d.demandDate?.split('T')[0] || d.demandDate || '';
      if (d.materialId && dStr && allowedMaterialIds.has(d.materialId)) byKey.set(key(d.materialId, dStr), d);
    });
    pendingList.forEach((d: DemandDto) => {
      const dStr = d.demandDate?.split('T')[0] || d.demandDate || '';
      if (d.materialId && dStr && allowedMaterialIds.has(d.materialId)) byKey.set(key(d.materialId, dStr), d);
    });
    localEditsRef.current.forEach((edit) => {
      if (edit.demandType === demandType && edit.materialId && allowedMaterialIds.has(edit.materialId)) {
        const k = key(edit.materialId, edit.dateStr);
        if (edit.quantity === null) {
          byKey.delete(k);
        } else {
          byKey.set(k, {
            materialId: edit.materialId,
            materialName: edit.materialName,
            demandDate: `${edit.dateStr}T00:00:00`,
            quantity: edit.quantity,
            demandType
          } as DemandDto);
        }
      }
    });
    const filteredMaterials = materials
      .filter((m: any) => m.materialType === materialFilter)
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return filteredMaterials.map((mat: any) => {
      const row: Record<string, any> = { materialId: mat.uuid, materialName: mat.name || '' };
      weekDateColumns.forEach(dateStr => {
        const d = byKey.get(key(mat.uuid, dateStr));
        row[dateStr] = d !== undefined ? (d.quantity ?? 0) : null;
        if (d?.uuid) row[`${dateStr}_uuid`] = d.uuid;
      });
      return row;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersInWeek, forecastsInWeek, materials, weekDateColumns]);

  const handleCellEdit = useCallback((
    demandType: 'ORDER' | 'FORECAST',
    materialId: string,
    materialName: string,
    dateStr: string,
    quantity: number | null,
    existingUuid?: string
  ) => {
    const editKey = `${demandType}|${materialId}|${dateStr}`;
    localEditsRef.current.set(editKey, { demandType, materialId, materialName, dateStr, quantity, existingUuid });
  }, []);

  const ordersGridData = useMemo(() => getGridDataForType('ORDER', 'PRODUCT'), [getGridDataForType]);
  const forecastsGridData = useMemo(() => getGridDataForType('FORECAST', 'PRODUCT'), [getGridDataForType]);
  const intermediatesGridData = useMemo(() => getGridDataForType('FORECAST', 'INTERMEDIATE_PRODUCT'), [getGridDataForType]);

  const importWhitelistUuids = useMemo(
    () => parseDemandImportOptions(demandsCsvConfig).allowedMaterialUuids,
    [demandsCsvConfig]
  );

  const handleDemandRowDataBound = useCallback((args: any) => {
    if (!importWhitelistUuids) return;
    const data = args?.data;
    if (!data || !args?.row) return;
    if (data.materialId && !importWhitelistUuids.has(data.materialId)) {
      args.row.classList.add('demand-row--not-importable');
    }
  }, [importWhitelistUuids]);
  const hasNoProducts = ordersGridData.length === 0;
  const hasNoIntermediates = intermediatesGridData.length === 0;

  const demandViewData = useMemo(
    () => convertDemandsForView(productionDisplayDemandsAggregated),
    [convertDemandsForView, productionDisplayDemandsAggregated]
  );

  // Main products only: the production quantities aggregate is per effectiveId (alias -> main); otherwise aliases produce empty rows.
  const allMaterialsForView = useMemo(
    () => materials
      .filter((m: any) => m.materialType === 'PRODUCT' && !m.isAlias)
      .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
      .map((m: any) => ({ id: m.uuid || '', name: m.name || '' })),
    [materials]
  );

  // Replanning: the new target quantity per materialId is the same production quantity value as in
  // regular planning. productionEffectiveDemands carries the PRODUCTION date (demandDate shifted
  // back by longTermOffsetDays for long-term managed products) and merges order over forecast.
  // The quantity on the canonical day (= plannedEnd) therefore equals the demand on the delivery
  // day (plannedEnd + offset). The key is the actual materialId (an alias stays the alias UUID).
  const newTargetByMaterial = useMemo(() => {
    const canonicalDate = (formData.plannedEnd || frozenZoneEnd || '')?.split('T')[0];
    const map = new Map<string, number>();
    if (!canonicalDate) return map;
    productionEffectiveDemands.forEach((d: DemandDto) => {
      if (!d.materialId) return;
      const dStr = d.demandDate?.split('T')[0] || '';
      if (dStr !== canonicalDate) return;
      map.set(d.materialId, (map.get(d.materialId) ?? 0) + (d.quantity ?? 0));
    });
    return map;
  }, [productionEffectiveDemands, formData.plannedEnd, frozenZoneEnd]);

  // Replanning: main products without aliases - alias quantities are aggregated into the main product
  const replanningProductMaterials = useMemo(
    () => filterReplanningProductMaterials(materials),
    [materials],
  );
  const replanningGridData = useMemo(() => {
    const canonicalDateStr = (formData.plannedEnd || frozenZoneEnd || '').split('T')[0] || '';
    return buildReplanningGridRows({
      replanningProductMaterials,
      producedQuantitiesRows,
      pendingOrders,
      canonicalDateStr,
      newTargetByMaterial,
    });
  }, [producedQuantitiesRows, newTargetByMaterial, replanningProductMaterials, formData.plannedEnd, frozenZoneEnd, pendingOrders]);

  // Replanning: refresh the Syncfusion grid when the data changes.
  // GridComponent does not update template columns automatically on a dataSource change -
  // refresh() forces the re-render without remounting the grid (the scroll position survives).
  useEffect(() => {
    if (!isReplanningView || !replanningGridRef.current) return;
    replanningGridRef.current.refresh();
  }, [replanningGridData, isReplanningView]);

  // Replanning: update the wizard context for the "Next" validation.
  // A new target below the actual only blocks UNLOCKED products (locked ones have fixed quantities).
  React.useEffect(() => {
    if (!isReplanningView || !setStep1ReplanningHasNewSollBelowIst) return;
    const hasNewSollBelowIst = replanningGridData.some(
      (r: { isNewTargetBelowActual: boolean; isLocked: boolean }) => r.isNewTargetBelowActual && !r.isLocked
    );
    setStep1ReplanningHasNewSollBelowIst(hasNewSollBelowIst);
  }, [isReplanningView, replanningGridData, setStep1ReplanningHasNewSollBelowIst]);

  // Replanning: a manual change of "new target" -> a pending order (one demand per material at the planning start).
  // The value is ALWAYS taken over (even when below the actual); validation happens on the click on "Next",
  // so that the user does not have to re-enter the value after every keystroke.
  // The offending row is marked visually (umplanung-row--newTargetBelowActual).
  const handleUmplanungNewSollEdit = useCallback((
    materialId: string,
    materialName: string,
    quantity: number,
    existingUuid: string | undefined,
    _actualQuantity: number
  ) => {
    void _actualQuantity;
    // REPLANNING: book onto the delivery day = production day (plannedEnd) + longTermOffsetDays,
    // so that productionEffectiveDemands shifts the value back to plannedEnd when reading.
    const deliveryDay = deliveryDateStrForMaterial(materialId);
    const dateStr = deliveryDay ? `${deliveryDay}T00:00:00` : '';
    if (!dateStr) return;
    const dto: DemandDto = {
      materialId,
      materialName,
      demandDate: dateStr,
      dueTime: undefined,
      quantity: quantity || 0,
      demandType: 'ORDER'
    };
    addOrUpdatePendingOrder(dto, existingUuid);
  }, [deliveryDateStrForMaterial, addOrUpdatePendingOrder]);

  const handleApplyFrozenZone = useCallback(() => {
    let applyValue = pendingFrozenZoneEnd ?? frozenZoneEnd;
    // Fallback: Syncfusion DateTimePicker may not always fire onChange; use current picker value if state is empty
    if (!applyValue && frozenZonePickerRef.current?.value) {
      const pickerDate = frozenZonePickerRef.current.value as Date;
      if (pickerDate && !isNaN(pickerDate.getTime())) {
        const y = pickerDate.getFullYear(), m = String(pickerDate.getMonth() + 1).padStart(2, '0'), day = String(pickerDate.getDate()).padStart(2, '0');
        const h = String(pickerDate.getHours()).padStart(2, '0'), min = String(pickerDate.getMinutes()).padStart(2, '0'), s = String(pickerDate.getSeconds()).padStart(2, '0');
        applyValue = `${y}-${m}-${day}T${h}:${min}:${s}`;
      }
    }
    if (!applyValue) {
      showToast('warning', t('common.validation', 'Validation'), t('productionSchedule.wizard.step1.frozenZoneEndRequired', 'Please select a date.'));
      return;
    }
    if (!sourceScheduleWindow) return;
    const d = new Date(applyValue);
    if (d.getTime() < sourceScheduleWindow.min.getTime() || d.getTime() > sourceScheduleWindow.max.getTime()) {
      showToast('warning', t('common.validation', 'Validation'), t('productionSchedule.wizard.step1.frozenZoneEndMustBeInPlanRange', "Frozen zone end must be within the active plan's planning period."));
      return;
    }
    setFrozenZoneEnd(applyValue);
    setPendingFrozenZoneEnd(null);
  }, [pendingFrozenZoneEnd, frozenZoneEnd, sourceScheduleWindow, setFrozenZoneEnd, showToast, t]);

  /** Formats a date as local ISO (YYYY-MM-DDTHH:mm:ss) without a time zone - for API comparison with database times. */
  const formatLocalDateTimeForApi = useCallback((d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}:${s}`;
  }, []);

  const formatPlanWindowDateTime = useCallback((date: Date) => {
    return date.toLocaleString(i18n.language.startsWith('de') ? 'de-DE' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [i18n.language]);

  // Replanning: only the frozen zone plus 4 columns (no tabs, no planning horizon) - as in the concept
  const umplanungContent = (
    <div className="flex flex-col flex-1 min-h-0 gap-6 p-2">
      <div className="flex-shrink-0 space-y-2">
        <div className="flex items-center gap-1.5">
          <label className="text-sm font-medium text-gray-700">
            {t('productionSchedule.wizard.step1.frozenZoneEndLabel', 'Frozen Zone End')} *
          </label>
          <span
            className="e-icons e-info inline-block text-blue-600 cursor-help align-middle text-base"
            title={t('productionSchedule.wizard.step1.frozenZoneEndHint', 'Select the frozen zone end – the table below will update with actual, old target, new target and remaining quantities.')}
            aria-label={t('productionSchedule.wizard.step1.frozenZoneEndHint')}
          />
        </div>
        {frozenZoneUnavailableReason === 'REPLAN_NOT_POSSIBLE' && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            {t('productionSchedule.wizard.step1.replanning.notPossibleUseContinuation', 'Der Plan kann nicht mehr umgeplant werden. Bitte nutzen Sie die Anschlussplanung.')}
          </p>
        )}
        {sourceScheduleWindow && (
          <p className="text-xs text-gray-600">
            {t('productionSchedule.wizard.step1.frozenZoneEndPlanRange', 'Frozen-Zone-Ende muss zwischen {{von}} und {{bis}} liegen.', {
              von: formatPlanWindowDateTime(sourceScheduleWindow.min),
              bis: formatPlanWindowDateTime(sourceScheduleWindow.max)
            })}
          </p>
        )}
        <div className="flex items-end gap-2 flex-wrap">
          <DateTimePickerComponent
            ref={frozenZonePickerRef}
            key={`frozen-zone-dp-${i18n.language}`}
            value={(pendingFrozenZoneEnd ?? frozenZoneEnd) ? new Date(pendingFrozenZoneEnd ?? frozenZoneEnd!) : undefined}
            onChange={(e: any) => {
              const d = e.value as Date | null;
              setPendingFrozenZoneEnd(d ? formatLocalDateTimeForApi(d) : null);
            }}
            format={i18n.language.startsWith('de') ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy HH:mm'}
            locale={i18n.language.startsWith('de') ? 'de-DE' : 'en'}
            firstDayOfWeek={1}
            cssClass="w-full max-w-xs"
            placeholder={t('productionSchedule.wizard.step0.frozenZoneEndPlaceholder', 'Date and time')}
            enabled={!!sourceScheduleWindow}
          />
          <ButtonComponent
            type="button"
            content={t('productionSchedule.wizard.step1.frozenZoneUpdate', 'Update')}
            cssClass="e-primary"
            disabled={!sourceScheduleWindow}
            ref={(btn: any) => { if (btn?.element) btn.element.onclick = () => handleApplyFrozenZone(); }}
          />
        </div>
      </div>
      <div className="flex flex-col flex-1 min-h-0 gap-2">
        <div className="flex items-center justify-between flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-800">
            {t('productionSchedule.wizard.step1.tab.productionQuantities', 'Produktionsmenge')}
          </h3>
          <ButtonComponent
            type="button"
            content={t('productionSchedule.wizard.step1.replanning.importOrders', 'Import Bestellungen')}
            cssClass="e-outline"
            ref={(btn: any) => { if (btn?.element) btn.element.onclick = () => setReplanningImportOpen(true); }}
          />
        </div>
      {loadingProducedQuantities ? (
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-gray-600 text-center">{t('common.loading', 'Loading...')}</p>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-hidden">
            <GridComponent
              key={`umplanung-grid-${frozenZoneEnd ?? 'none'}`}
              ref={replanningGridRef}
              dataSource={replanningGridData}
              height="100%"
              allowSorting={false}
              locale={i18n.language.startsWith('de') ? 'de-DE' : 'en'}
              created={() => {
                // Grid rendering after the first mount (e.g. after choosing the frozen zone): force a resize
                window.dispatchEvent(new Event('resize'));
              }}
              rowDataBound={(args: any) => {
                const data = args?.data;
                if (!data || !args?.row) return;
                if (data.isLockedAndExceeded) args.row.classList.add('umplanung-row--lockedAndExceeded');
                else if (data.isNewTargetBelowActual) args.row.classList.add('umplanung-row--newTargetBelowActual');
                if (data.campaignStatus === 'COMPLETED') args.row.classList.add('umplanung-row--completed');
                else if (data.campaignStatus === 'ACTIVE') args.row.classList.add('umplanung-row--active');
                if (data.isLocked) args.row.classList.add('umplanung-row--locked');
              }}
            >
              <ColumnsDirective>
                <ColumnDirective field="materialName" headerText={t('demandView.grid.product', 'Produkt')} width="180" textAlign="Left" headerTextAlign="Left" />
                <ColumnDirective
                  field="campaignStatus"
                  headerText={t('productionSchedule.wizard.step1.replanning.campaignStatus', 'Kampagnen-Status')}
                  width="140"
                  textAlign="Center"
                  headerTextAlign="Center"
                  template={(props: any) => {
                    const data = props.data || props.rowData || props;
                    const status: 'OPEN' | 'ACTIVE' | 'COMPLETED' = data?.campaignStatus ?? 'OPEN';
                    const labelKey = status === 'COMPLETED'
                      ? 'productionSchedule.wizard.step1.replanning.statusCompleted'
                      : status === 'ACTIVE'
                        ? 'productionSchedule.wizard.step1.replanning.statusActive'
                        : 'productionSchedule.wizard.step1.replanning.statusOpen';
                    const labelDefault = status === 'COMPLETED' ? 'abgeschlossen' : status === 'ACTIVE' ? 'aktiv' : 'offen';
                    return <span className={`umplanung-status-badge umplanung-status-badge--${status.toLowerCase()}`}>{t(labelKey, labelDefault)}</span>;
                  }}
                />
                <ColumnDirective
                  field="startedDoughBatchCount"
                  headerText={t('productionSchedule.wizard.step1.replanning.startedDoughBatches', 'Begonnene Teig-Chargen')}
                  width="160"
                  textAlign="Center"
                  headerTextAlign="Center"
                  template={(props: any) => {
                    const data = props.data || props.rowData || props;
                    const started = data?.startedDoughBatchCount ?? 0;
                    const total = data?.totalDoughBatchCount ?? 0;
                    return <span>{started}/{total}</span>;
                  }}
                />
                <ColumnDirective field="actualQuantity" headerText={t('productionSchedule.wizard.step1.replanning.actualQuantity', 'Ist-Menge')} width="90" type="number" format="N0" textAlign="Center" headerTextAlign="Center" />
                <ColumnDirective field="oldTargetQuantity" headerText={t('productionSchedule.wizard.step1.replanning.oldTargetQuantity', 'Alte Soll-Menge')} width="110" type="number" format="N0" textAlign="Center" headerTextAlign="Center" />
                <ColumnDirective
                  field="newTargetQuantity"
                  headerText={t('productionSchedule.wizard.step1.replanning.newTargetQuantity', 'Neue Soll-Menge')}
                  width="110"
                  textAlign="Center"
                  headerTextAlign="Center"
                  template={(props: any) => {
                    const data = props.data || props.rowData || props;
                    const isLocked = data?.isLocked ?? false;
                    const isInvalid = !isLocked && (data?.isNewTargetBelowActual ?? false);
                    // Locked products: show the old target as the plan value (not editable).
                    // newTargetQuantity is already set to oldTargetQuantity in replanningGridUtils,
                    // so the display here is consistent.
                    const qty = data?.newTargetQuantity ?? 0;
                    const inputClass = isLocked
                      ? 'demand-grid-input-no-spinner w-full px-2 py-1 text-center border rounded bg-gray-100 cursor-not-allowed'
                      : isInvalid
                        ? 'demand-grid-input-no-spinner w-full px-2 py-1 text-center border rounded umplanung-soll-input--invalid'
                        : 'demand-grid-input-no-spinner w-full px-2 py-1 text-center border rounded border-gray-300';
                    return (
                      <input
                        key={`${data?.materialId}-${qty}-${isLocked}`}
                        type="number"
                        min={data?.actualQuantity ?? 0}
                        step="1"
                        className={inputClass}
                        defaultValue={qty}
                        disabled={isLocked}
                        readOnly={isLocked}
                        title={isInvalid
                          ? t('productionSchedule.wizard.step1.replanning.newSollBelowIst', 'Neue Soll-Menge darf nicht unter der Ist-Menge liegen.')
                          : undefined}
                        onBlur={(e) => {
                          if (isLocked) return;
                          const v = parseFloat((e.target as HTMLInputElement).value);
                          if (data?.materialId != null) {
                            handleUmplanungNewSollEdit(
                              data.materialId,
                              data.materialName || '',
                              isNaN(v) ? 0 : v,
                              data.newTargetUuid,
                              data.actualQuantity ?? 0
                            );
                          }
                        }}
                      />
                    );
                  }}
                />
                <ColumnDirective
                  field="deviationQuantity"
                  headerText={t('productionSchedule.wizard.step1.replanning.deviation', 'Abweichung')}
                  width="120"
                  textAlign="Center"
                  headerTextAlign="Center"
                  template={(props: any) => {
                    const data = props.data || props.rowData || props;
                    const dev = data?.deviationQuantity ?? 0;
                    const isOvermenge = data?.isOvermenge ?? false;
                    const overmenge = data?.overmengeQuantity ?? 0;
                    const isNegative = dev < 0;
                    if (isOvermenge) {
                      return (
                        <span
                          className="umplanung-overmenge-badge"
                          title={t('productionSchedule.wizard.step1.replanning.overmengeTooltip', 'Ist-Menge uebersteigt das urspruenglich Geplante: +{{count}} Stueck', { count: overmenge })}
                        >
                          {t('productionSchedule.wizard.step1.replanning.overmengeBadge', 'Übermenge +{{count}}', { count: overmenge })}
                        </span>
                      );
                    }
                    return (
                      <span className={isNegative ? 'text-red-600 font-medium' : ''}>
                        {dev}
                      </span>
                    );
                  }}
                />
              </ColumnsDirective>
              <Inject services={[]} />
            </GridComponent>
          </div>
        </div>
      )}

      {replanningImportOpen && (
        <CsvImportDialog<DemandDto>
          isOpen={replanningImportOpen}
          onClose={() => setReplanningImportOpen(false)}
          onImport={handleReplanningImport}
          entityFields={demandEntityFields}
          entityName="demand"
          entityType="demands"
          importOptions={{
            allowPartialImport: true,
            batchSize: 25,
            validateBeforeImport: true,
            showDetailedErrors: true,
            materialsForLookup: materials,
            deferredSave: true,
            fixedRunParameterValues: (() => {
              const d = (formData.plannedEnd || frozenZoneEnd || '')?.split('T')[0];
              return d ? { demandDate: d } : ({} as Record<string, string>);
            })()
          }}
        />
      )}
      </div>
    </div>
  );

  if (planningMode === 'REPLANNING') {
    return <div className="flex flex-col flex-1 min-h-0 overflow-hidden">{umplanungContent}</div>;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <TabComponent
        ref={tabRef}
        cssClass="wizard-step1-tabs wizard-step1-tabs--headers-only"
        animation={{ previous: { effect: 'None' }, next: { effect: 'None' } }}
        selected={(e: any) => {
          const idx = e.selectedIndex;
          if (idx === undefined) return;
          activeTabRef.current = idx;

          if (idx === 0) flushToContext();

          [panel0Ref, panel1Ref, panel2Ref, panel3Ref].forEach((ref, i) => {
            if (ref.current) ref.current.style.display = i === idx ? 'flex' : 'none';
          });

          setVisitedTabs(prev => {
            if (prev.has(idx)) return prev;
            return new Set(prev).add(idx);
          });

          requestAnimationFrame(() => {
            if (idx === 1 && ordersGridRef.current) ordersGridRef.current.refresh();
            if (idx === 2 && forecastsGridRef.current) forecastsGridRef.current.refresh();
            if (idx === 3 && intermediatesGridRef.current) intermediatesGridRef.current.refresh();
          });
        }}
      >
        <TabItemsDirective>
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step1.tab.productionQuantities', 'Production Quantities') }} />
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step1.tab.orders', 'Orders') }} />
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step1.tab.forecasts', 'Forecasts') }} />
          <TabItemDirective header={{ text: t('productionSchedule.wizard.step1.tab.intermediates', 'Vorprodukte') }} />
        </TabItemsDirective>
      </TabComponent>

      <div ref={panel0Ref} className="flex flex-col flex-1 min-h-0 gap-6 p-2">
        <div className="flex items-center justify-between gap-4 flex-shrink-0">
          <div className="grid grid-cols-2 gap-4 flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('productionSchedule.form.plannedStart')}
              </label>
              <DatePickerComponent
                value={formData.plannedStart ? new Date(formData.plannedStart) : new Date(tomorrow)}
                min={tomorrow}
                max={maxStartDate}
                strictMode={true}
                firstDayOfWeek={1}
                enabled={!!planningSettings.allowOverride}
                change={(e: any) => {
                  if (e?.value) {
                    const date = e.value instanceof Date ? e.value : new Date(e.value);
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    const startStr = `${y}-${m}-${d}T00:00:00`;
                    updateFormData('plannedStart', startStr);
                    if (isSingleDayHorizon) {
                      updateFormData('plannedEnd', `${y}-${m}-${d}T23:59:59`);
                    }
                  }
                }}
                cssClass="w-full"
                format={i18n.language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'}
                locale={i18n.language === 'de' ? 'de-DE' : 'en'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('productionSchedule.form.plannedEnd')}
              </label>
              <DatePickerComponent
                value={formData.plannedEnd ? new Date(formData.plannedEnd) : undefined}
                min={formData.plannedStart ? new Date(formData.plannedStart.split('T')[0] + 'T00:00:00') : tomorrow}
                max={formData.plannedStart && planningSettings.maxDays > 1 ? (() => {
                  const start = new Date(formData.plannedStart!.split('T')[0] + 'T00:00:00');
                  start.setDate(start.getDate() + planningSettings.maxDays - 1);
                  return start;
                })() : undefined}
                strictMode={true}
                firstDayOfWeek={1}
                onChange={(e: any) => {
                  if (e.value && !isSingleDayHorizon) {
                    const date = e.value instanceof Date ? e.value : new Date(e.value);
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    updateFormData('plannedEnd', `${y}-${m}-${d}T23:59:59`);
                  }
                }}
                cssClass="w-full"
                format={i18n.language === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'}
                locale={i18n.language === 'de' ? 'de-DE' : 'en'}
                enabled={planningSettings.allowOverride && !isSingleDayHorizon}
              />
              {!planningSettings.allowOverride && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('systemSettings.planningHorizon.allowOverrideHelp')}
                </p>
              )}
              {isSingleDayHorizon && (
                <p className="text-xs text-gray-500 mt-1">
                  {t('productionSchedule.wizard.step1.singleDayHorizonHint', 'With a maximum 1-day planning horizon, end equals start.')}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col">
          {isLoadingDemands ? (
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-gray-600 text-center">{t('common.loading', 'Loading...')}</p>
            </div>
          ) : (
            <DemandView
              startDate={displayWeekStartStr}
              planningHorizonStart={planningHorizonStart}
              planningHorizonEnd={planningHorizonEnd}
              demands={demandViewData}
              allMaterials={allMaterialsForView}
              fillHeight
            />
          )}
        </div>
      </div>

      <div ref={panel1Ref} className="flex flex-col flex-1 min-h-0 p-2">
        {visitedTabs.has(1) && (
          <>
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <h4 className="text-lg font-semibold text-gray-900">
                {t('productionSchedule.wizard.step1.tab.orders', 'Orders')}
              </h4>
              <ButtonComponent
                type="button"
                ref={(btn: any) => { if (btn?.element) btn.element.onclick = () => setImportDialogType('orders'); }}
                cssClass="e-primary"
                disabled={!hasDemandCreatePermission}
              >
                {t('csvImport.import')}
              </ButtonComponent>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              {isLoadingDemands ? (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-600 text-center">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : hasNoProducts ? (
                <div className="border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-600 text-center">
                    {t('productionSchedule.wizard.step1.noProductsForDemands', 'No products (make-to-stock) available. Please create products first under Administration → Material Data → Products.')}
                  </p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 border border-gray-200 rounded-lg">
                  <GridComponent
                    ref={ordersGridRef}
                    locale="de-DE"
                    key={`orders-grid-${weekDateColumns[0]}`}
                    dataSource={ordersGridData}
                    height="100%"
                    allowSorting={false}
                    rowDataBound={handleDemandRowDataBound}
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="materialName" headerText={t('demandView.grid.product', 'Produkt')} width="180" allowEditing={false} />
                      {weekDateColumns.map(dateStr => (
                        <ColumnDirective
                          key={dateStr}
                          field={dateStr}
                          headerText={formatDateForGrid(dateStr)}
                          width="90"
                          type="number"
                          format="N0"
                          template={(props: any) => {
                            const data = props.data || props.rowData || props;
                            const cellVal = data?.[dateStr];
                            const displayEmpty = cellVal === null || cellVal === undefined;
                            // Capture live into localEditsRef (see the forecasts grid): protects the typed
                            // value from being lost on a re-render or reload while typing.
                            const commitCell = (rawValue: string) => {
                              if (!data?.materialId) return;
                              const existingUuid = data?.[`${dateStr}_uuid`];
                              const value = parseDemandCellInput(rawValue);
                              handleCellEdit('ORDER', data.materialId, data.materialName || '', dateStr, value, existingUuid);
                            };
                            return (
                              <input
                                key={`${data?.materialId}-${dateStr}-${String(cellVal)}-${data?.[`${dateStr}_uuid`] ?? ''}`}
                                type="number"
                                min="0"
                                step="1"
                                className="demand-grid-input-no-spinner w-full px-2 py-1 text-right border border-gray-300 rounded"
                                placeholder=""
                                defaultValue={displayEmpty ? '' : String(cellVal)}
                                onChange={(e) => commitCell((e.target as HTMLInputElement).value)}
                                onBlur={(e) => commitCell((e.target as HTMLInputElement).value)}
                              />
                            );
                          }}
                        />
                      ))}
                    </ColumnsDirective>
                    <Inject services={[]} />
                  </GridComponent>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div ref={panel2Ref} className="flex flex-col flex-1 min-h-0 p-2">
        {visitedTabs.has(2) && (
          <>
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <h4 className="text-lg font-semibold text-gray-900">
                {t('productionSchedule.wizard.step1.tab.forecasts', 'Forecasts')}
              </h4>
              <ButtonComponent
                type="button"
                ref={(btn: any) => { if (btn?.element) btn.element.onclick = () => setImportDialogType('forecasts'); }}
                cssClass="e-primary"
                disabled={!hasDemandCreatePermission}
              >
                {t('csvImport.import')}
              </ButtonComponent>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              {isLoadingDemands ? (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-600 text-center">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : hasNoProducts ? (
                <div className="border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-600 text-center">
                    {t('productionSchedule.wizard.step1.noProductsForDemands', 'No products (make-to-stock) available. Please create products first under Administration → Material Data → Products.')}
                  </p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 border border-gray-200 rounded-lg">
                  <GridComponent
                    ref={forecastsGridRef}
                    locale="de-DE"
                    key={`forecasts-grid-${weekDateColumns[0]}`}
                    dataSource={forecastsGridData}
                    height="100%"
                    allowSorting={false}
                    rowDataBound={handleDemandRowDataBound}
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="materialName" headerText={t('demandView.grid.product', 'Produkt')} width="180" />
                      {weekDateColumns.map(dateStr => (
                        <ColumnDirective
                          key={dateStr}
                          field={dateStr}
                          headerText={formatDateForGrid(dateStr)}
                          width="90"
                          type="number"
                          format="N0"
                          template={(props: any) => {
                            const data = props.data || props.rowData || props;
                            const cellVal = data?.[dateStr];
                            const displayEmpty = cellVal === null || cellVal === undefined;
                            // Write live into localEditsRef (not only on blur), so that the typed value
                            // survives every grid rebuild: getGridDataForType folds localEditsRef back
                            // into the dataSource. Otherwise the value currently focused is lost when a
                            // reload or re-render happens while typing.
                            const commitCell = (rawValue: string) => {
                              if (!data?.materialId) return;
                              const existingUuid = data?.[`${dateStr}_uuid`];
                              const value = parseDemandCellInput(rawValue);
                              handleCellEdit('FORECAST', data.materialId, data.materialName || '', dateStr, value, existingUuid);
                            };
                            return (
                              <input
                                key={`${data?.materialId}-${dateStr}-${String(cellVal)}-${data?.[`${dateStr}_uuid`] ?? ''}`}
                                type="number"
                                min="0"
                                step="1"
                                className="demand-grid-input-no-spinner w-full px-2 py-1 text-right border border-gray-300 rounded"
                                placeholder=""
                                defaultValue={displayEmpty ? '' : String(cellVal)}
                                onChange={(e) => commitCell((e.target as HTMLInputElement).value)}
                                onBlur={(e) => commitCell((e.target as HTMLInputElement).value)}
                              />
                            );
                          }}
                        />
                      ))}
                    </ColumnsDirective>
                    <Inject services={[]} />
              </GridComponent>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div ref={panel3Ref} className="flex flex-col flex-1 min-h-0 p-2">
        {visitedTabs.has(3) && (
          <>
            <div className="flex items-center justify-between mb-3 flex-shrink-0">
              <h4 className="text-lg font-semibold text-gray-900">
                {t('productionSchedule.wizard.step1.tab.intermediates', 'Vorprodukte')}
              </h4>
              <div className="text-xs text-gray-500">
                {t(
                  'productionSchedule.wizard.step1.intermediates.hint',
                  'Eintraege werden bis zum Planungsende auf den gleichen Wochentag fortgeschrieben.',
                )}
              </div>
            </div>
            <div className="flex-1 min-h-0 flex flex-col">
              {isLoadingDemands ? (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-600 text-center">{t('common.loading', 'Loading...')}</p>
                </div>
              ) : hasNoIntermediates ? (
                <div className="border border-gray-200 rounded-lg p-6">
                  <p className="text-gray-600 text-center">
                    {t(
                      'productionSchedule.wizard.step1.noIntermediates',
                      'Keine Vorprodukte angelegt. Bitte zuerst unter Stammdaten → Material → Vorprodukte anlegen.',
                    )}
                  </p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 border border-gray-200 rounded-lg">
                  <GridComponent
                    ref={intermediatesGridRef}
                    locale="de-DE"
                    key={`intermediates-grid-${weekDateColumns[0]}`}
                    dataSource={intermediatesGridData}
                    height="100%"
                    allowSorting={false}
                  >
                    <ColumnsDirective>
                      <ColumnDirective field="materialName" headerText={t('demandView.grid.product', 'Produkt')} width="180" />
                      {weekDateColumns.map(dateStr => (
                        <ColumnDirective
                          key={dateStr}
                          field={dateStr}
                          headerText={formatDateForGrid(dateStr)}
                          width="90"
                          type="number"
                          format="N0"
                          template={(props: any) => {
                            const data = props.data || props.rowData || props;
                            const cellVal = data?.[dateStr];
                            const displayEmpty = cellVal === null || cellVal === undefined;
                            // Capture live into localEditsRef (see the forecasts grid): protects the typed
                            // value from being lost on a re-render or reload while typing.
                            const commitCell = (rawValue: string) => {
                              if (!data?.materialId) return;
                              const existingUuid = data?.[`${dateStr}_uuid`];
                              const value = parseDemandCellInput(rawValue, { decimals: true });
                              handleCellEdit('FORECAST', data.materialId, data.materialName || '', dateStr, value, existingUuid);
                            };
                            return (
                              <input
                                key={`${data?.materialId}-${dateStr}-${String(cellVal)}-${data?.[`${dateStr}_uuid`] ?? ''}`}
                                type="number"
                                min="0"
                                step="any"
                                className="demand-grid-input-no-spinner w-full px-2 py-1 text-right border border-gray-300 rounded"
                                placeholder=""
                                defaultValue={displayEmpty ? '' : String(cellVal)}
                                onChange={(e) => commitCell((e.target as HTMLInputElement).value)}
                                onBlur={(e) => commitCell((e.target as HTMLInputElement).value)}
                              />
                            );
                          }}
                        />
                      ))}
                    </ColumnsDirective>
                    <Inject services={[]} />
                  </GridComponent>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {importDialogType && (
        <CsvImportDialog<DemandDto>
          isOpen={!!importDialogType}
          onClose={() => setImportDialogType(null)}
          onImport={(data) => handleImportDemands(data, importDialogType === 'orders' ? 'ORDER' : 'FORECAST')}
          entityFields={demandEntityFields}
          entityName="demand"
          entityType="demands"
          importOptions={{
            allowPartialImport: true,
            batchSize: 25,
            validateBeforeImport: true,
            showDetailedErrors: true,
            materialsForLookup: materials,
            deferredSave: true
          }}
        />
      )}
    </div>
  );
};

export default PlanningHorizon;
